import React, { FC, useEffect, useState } from "react";
import { IPaper } from "@deltasge/marauders-map";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import * as classesForm from "assets/styles/form";
import { useForm } from "react-hook-form";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import { apiRelatorio } from "configs";
import { NumericFormat } from "react-number-format";
import { IConfigPapelPadrao } from "store/modules/relatorios/configuracoes/papel-padrao";

export const getDefault = (item?: IConfigPapelPadrao): IConfigPapelPadrao => {
  return {
    papel: item?.papel || "A4",
    width: item?.width || 210,
    height: item?.height || 297,
    marginTop: item?.marginTop || 10,
    marginBottom: item?.marginBottom || 10,
    marginLeft: item?.marginLeft || 10,
    marginRight: item?.marginRight || 10,
    orientacao: item?.orientacao || "retrato",
  };
};

export const PageConfig: FC<{
  item?: IConfigPapelPadrao;
  handleClose: (args?: IConfigPapelPadrao) => void;
}> = ({ handleClose, item }) => {
  const [loading, setLoading] = useState(false);
  const [papeis, setPapeis] = useState<IPaper[]>([]);

  const { handleSubmit, setValue, watch } = useForm<IConfigPapelPadrao>({
    defaultValues: getDefault(item),
  });

  const onSubmit = async (data: IConfigPapelPadrao) => {
    handleClose(data);
  };

  useEffect(() => {
    (async () => {
      try {
        if (papeis.length == 0) {
          setLoading(true);
          const { data } = await apiRelatorio.get<IPaper[]>("tamanho-papel");
          if (data && data.length > 0) {
            setPapeis(data);
          }
        }
      } catch (error) {
        snack.error(getError(error));
      } finally {
        setLoading(false);
      }
    })();
  }, [papeis.length]);

  const papel = watch("papel");
  const orientacao = watch("orientacao");
  const width = watch("width");
  const height = watch("height");
  const marginTop = watch("marginTop");
  const marginBottom = watch("marginBottom");
  const marginLeft = watch("marginLeft");
  const marginRight = watch("marginRight");

  const selectedPapel = papeis.find((f) => f.name == papel);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <DialogContent dividers>
        <Box sx={{ ...classesForm.fieldset, p: 1, mb: 1 }}>
          <Typography variant="subtitle1">Opções</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                value={papel}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                label="Papel"
                select
                onChange={({ target: { value } }) => {
                  const papel = papeis.find((f) => f.name == value);
                  if (papel) {
                    setValue("width", papel.width);
                    setValue("height", papel.height);
                  }
                }}
                InputProps={{
                  endAdornment: !loading ? undefined : (
                    <InputAdornment position="end" sx={{ mr: 3 }}>
                      <CircularProgress
                        sx={({ spacing }) => ({
                          height: spacing(3).concat("!important"),
                          width: spacing(3).concat("!important"),
                        })}
                      />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem>Nenhum</MenuItem>
                {papeis.map((option) => (
                  <MenuItem key={`papel-${option.name}`} value={option.name}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControl>
                <RadioGroup
                  row
                  name="orientacao"
                  value={orientacao}
                  onChange={({ target: { value } }) => {
                    if (value && (value == "paisagem" || value == "retrato")) {
                      setValue("orientacao", value);
                    }
                    if (value === "paisagem" && selectedPapel) {
                      setValue("height", selectedPapel.height);
                      setValue("width", selectedPapel.width);
                    } else if (selectedPapel) {
                      setValue("height", selectedPapel.width);
                      setValue("width", selectedPapel.height);
                    }
                  }}
                >
                  <FormControlLabel
                    value={"retrato"}
                    control={<Radio />}
                    label="Retrato"
                  />
                  <FormControlLabel
                    value={"paisagem"}
                    control={<Radio />}
                    label="Paisagem"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <NumericFormat
                suffix=" mm"
                label="Largura"
                customInput={TextField}
                value={width}
                onValueChange={({ floatValue }) => {
                  if (floatValue) setValue("width", floatValue);
                }}
                allowNegative={false}
                thousandSeparator={false}
                disabled={selectedPapel?.name != "Customizado"}
              />
            </Grid>
            <Grid item xs={6}>
              <NumericFormat
                suffix=" mm"
                label="Altura"
                customInput={TextField}
                value={height}
                onValueChange={({ floatValue }) => {
                  if (floatValue) setValue("height", floatValue);
                }}
                allowNegative={false}
                thousandSeparator={false}
                disabled={selectedPapel?.name != "Customizado"}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ ...classesForm.fieldset, p: 1, mb: 1 }}>
                <Typography variant="subtitle1">Margens</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <NumericFormat
                      suffix=" mm"
                      label="Topo"
                      customInput={TextField}
                      value={marginTop}
                      onValueChange={({ floatValue }) => {
                        if (floatValue) setValue("marginTop", floatValue);
                      }}
                      allowNegative={false}
                      thousandSeparator={false}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumericFormat
                      suffix=" mm"
                      label="Base"
                      customInput={TextField}
                      value={marginBottom}
                      onValueChange={({ floatValue }) => {
                        if (floatValue) setValue("marginBottom", floatValue);
                      }}
                      allowNegative={false}
                      thousandSeparator={false}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumericFormat
                      suffix=" mm"
                      label="Esquerda"
                      customInput={TextField}
                      value={marginLeft}
                      onValueChange={({ floatValue }) => {
                        if (floatValue) setValue("marginLeft", floatValue);
                      }}
                      allowNegative={false}
                      thousandSeparator={false}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumericFormat
                      suffix=" mm"
                      label="Direita"
                      customInput={TextField}
                      value={marginRight}
                      onValueChange={({ floatValue }) => {
                        if (floatValue) setValue("marginRight", floatValue);
                      }}
                      allowNegative={false}
                      thousandSeparator={false}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={classesForm.submit}>
        <Button onClick={() => handleClose()}>Fechar</Button>
        <Button type="submit">Salvar</Button>
      </DialogActions>
    </form>
  );
};
